/**
 * Validation of the automatic-captions settings (core/model.ts `CaptionSettings`), applied when a
 * document is loaded and when the UI edits a field.
 */
import { CAPTION_LANG_MAX, CAPTION_LANG_PATTERN, type CaptionSettings } from './model';

const FALLBACK_LANG = 'en-US';
const SOURCE_ID_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;

export function isCaptionLang(v: unknown): v is string {
  return typeof v === 'string' && v.length <= CAPTION_LANG_MAX && CAPTION_LANG_PATTERN.test(v);
}

/** The browser's language when it is a usable tag, else en-US. */
export function defaultCaptionLang(): string {
  const lang = typeof navigator === 'undefined' ? undefined : navigator.language;
  return isCaptionLang(lang) ? lang : FALLBACK_LANG;
}

/** A trimmed tag, or `fallback` when it is not one. */
export function cleanCaptionLang(v: unknown, fallback = defaultCaptionLang()): string {
  const text = typeof v === 'string' ? v.trim() : '';
  return isCaptionLang(text) ? text : fallback;
}

export function defaultCaptions(): CaptionSettings {
  return { enabled: false, lang: defaultCaptionLang(), sourceId: null };
}

export function cleanCaptions(v: unknown): CaptionSettings {
  const c = typeof v === 'object' && v !== null && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
  return {
    enabled: c.enabled === true,
    lang: cleanCaptionLang(c.lang),
    sourceId: typeof c.sourceId === 'string' && SOURCE_ID_PATTERN.test(c.sourceId) ? c.sourceId : null,
  };
}
